import {
  faFacebookF,
  faLinkedinIn,
  faRedditAlien,
  faTelegram,
  faWhatsapp,
  faXTwitter,
} from '@fortawesome/free-brands-svg-icons';
import { ReactComponent as FacebookIcon } from 'src/images/svgs/socials/facebook.svg';
import { ReactComponent as LinkedinIcon } from 'src/images/svgs/socials/linkedin.svg';
import { ReactComponent as RedditIcon } from 'src/images/svgs/socials/reddit.svg';
import { ReactComponent as TelegramIcon } from 'src/images/svgs/socials/telegram.svg';
import { ReactComponent as WhatsAppIcon } from 'src/images/svgs/socials/whatsapp.svg';
import { ReactComponent as TwitterXIcon } from 'src/images/svgs/socials/x-twitter.svg';
import { SocialPlatformId, THM_SOCIAL_HANDLES } from '../components/social-share/social-share.constants';
import { THM_USER_ID_KEY } from '../constants';
import { gb } from '../growthbook';
import type { SegmentShareEvent } from '../types';
import { type LinkProps, type SocialIconLinks, SocialName, SocialOfficialName } from './page-share.types';

const addUtmParams = (
  url: string,
  utmSource: string,
  utmContent: string,
  utmCampaign = 'social_share',
  utmMedium = 'social',
): string => {
  const separator = url.includes('?') ? '&' : '?';

  let params = `${url}${separator}utm_source=${utmSource}&utm_medium=${utmMedium}&utm_campaign=${utmCampaign}&utm_content=${utmContent}`;

  const isSharerIdEnabled = gb.isOn('tracking-sharer-id');
  if (isSharerIdEnabled) {
    const sharerId = localStorage.getItem(THM_USER_ID_KEY);
    if (sharerId) params = `${params}&sharerId=${sharerId}`;
  }

  return params;
};

const createSharingUrl = {
  twitter: (url: string, description: string) =>
    `https://x.com/intent/post?url=${encodeURIComponent(url)}&text=${encodeURIComponent(
      description,
    )}&via=${THM_SOCIAL_HANDLES[SocialPlatformId.TWITTER]}&hashtags=tryhackme`,
  linkedin: (url: string, description: string) =>
    `https://www.linkedin.com/sharing/share-offsite/?mini=true&url=${encodeURIComponent(url)}&text=${encodeURIComponent(
      description,
    )}`,
  facebook: (url: string, description: string) =>
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&t=${encodeURIComponent(description)}`,
  whatsapp: (url: string, description: string) =>
    `https://wa.me/?text=${encodeURIComponent(description)}%0A${encodeURIComponent(url)}`,
  reddit: (url: string, description: string) =>
    `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(description)}`,
  telegram: (url: string, description: string) =>
    `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(description)}`,
};

export const socialIconLinks: Record<SocialName, SocialIconLinks> = {
  [SocialName.twitter]: {
    href: ({ url, description, campaign: { utmCampaign, utmContent, utmMedium } }: LinkProps) =>
      createSharingUrl.twitter(addUtmParams(url, 'twitter', utmContent, utmCampaign, utmMedium), description),
    icon: TwitterXIcon,
    faIcon: faXTwitter,
    name: SocialName.twitter,
    officialName: SocialOfficialName.twitter,
  },
  [SocialName.linkedin]: {
    href: ({ url, description, campaign: { utmCampaign, utmContent, utmMedium } }: LinkProps) =>
      createSharingUrl.linkedin(addUtmParams(url, 'linkedin', utmContent, utmCampaign, utmMedium), description),
    icon: LinkedinIcon,
    faIcon: faLinkedinIn,
    name: SocialName.linkedin,
    officialName: SocialOfficialName.linkedin,
  },
  [SocialName.facebook]: {
    href: ({ url, description, campaign: { utmCampaign, utmContent, utmMedium } }: LinkProps) =>
      createSharingUrl.facebook(addUtmParams(url, 'facebook', utmContent, utmCampaign, utmMedium), description),
    icon: FacebookIcon,
    faIcon: faFacebookF,
    name: SocialName.facebook,
    officialName: SocialOfficialName.facebook,
  },
  [SocialName.whatsapp]: {
    href: ({ url, description, campaign: { utmCampaign, utmContent, utmMedium } }: LinkProps) =>
      createSharingUrl.whatsapp(addUtmParams(url, 'whatsapp', utmContent, utmCampaign, utmMedium), description),
    icon: WhatsAppIcon,
    faIcon: faWhatsapp,
    name: SocialName.whatsapp,
    officialName: SocialOfficialName.whatsapp,
  },
  [SocialName.reddit]: {
    href: ({ url, description, campaign: { utmCampaign, utmContent, utmMedium } }: LinkProps) =>
      createSharingUrl.reddit(addUtmParams(url, 'reddit', utmContent, utmCampaign, utmMedium), description),
    icon: RedditIcon,
    faIcon: faRedditAlien,
    name: SocialName.reddit,
    officialName: SocialOfficialName.reddit,
  },
  [SocialName.telegram]: {
    href: ({ url, description, campaign: { utmCampaign, utmContent, utmMedium } }: LinkProps) =>
      createSharingUrl.telegram(addUtmParams(url, 'telegram', utmContent, utmCampaign, utmMedium), description),
    icon: TelegramIcon,
    faIcon: faTelegram,
    name: SocialName.telegram,
    officialName: SocialOfficialName.telegram,
  },
};

export const socialTrackEvents: Record<SocialName, SegmentShareEvent> = {
  [SocialName.twitter]: 'share-twitter',
  [SocialName.facebook]: 'share-facebook',
  [SocialName.linkedin]: 'share-linkedin',
  [SocialName.whatsapp]: 'share-whatsapp',
  [SocialName.reddit]: 'share-reddit',
  [SocialName.telegram]: 'share-telegram',
};
